import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../../../utils/userStore';
import { useGamificationStore } from '../../../../utils/gamificationStore';
import { useAllMissionsStore } from '../../../../utils/allMissionsStore';
import { useChatStore } from '../../../../utils/chatStore';
import { useAgentChat } from '../../../../hooks/useAgentChat';
import { useLayoutContext } from '../../../../layouts/general-layout';
import { RouterConstant } from '../../../../routers/index';
import { MissionItem } from '../../../../services/missions/missions.service';
import { UserService } from '../../../../services/user/user.service';
import { ChatService } from '../../../../services/chat/chat.services';


export const useDashboard = () => {
  const { t, openModesModal } = useLayoutContext();
  const navigate = useNavigate();
  
  // Stores
  const { userInfo, user_attributes } = useUserStore();
  const { 
    currentStreak, 
    totalPoints, 
    lastActivityDate, 
    initState: initGamification, 
    isLoading: loadingGamification 
  } = useGamificationStore();
  
  const { 
    openMissions: progressMissions, 
    isLoading: loadingMissions, 
    fetchAllMissions: fetchMissions
  } = useAllMissionsStore();

  // Chat stores and AI Agent V2 Hooks - Synchronized
  const { 
    messages, 
    returningMetadata, 
    setReturningMetadata, 
    addUserMessage, 
    addAssistantMessage,
    dashboardState,
    setDashboardState,
    retroStep,
    setRetroStep,
    showGreeting,
    setShowGreeting,
    showRetroText,
    setShowRetroText,
    showQuickReplies,
    setShowQuickReplies,
    quickReplyOptions,
    setQuickReplyOptions
  } = useChatStore();
  const { isLoading: isAgentLoading, talk } = useAgentChat();

  // Returning User & Retrospective States
  const [loadingMetadata, setLoadingMetadata] = useState(false); // Instant load
  
  // Sequential flow states
  const [isGreetingTyping, setIsGreetingTyping] = useState(true);
  const [loadingRetroText, setLoadingRetroText] = useState(false);
  const [isAgentLoadingManual, setIsAgentLoadingManual] = useState(false);

  const isReturningUser = useMemo(() => {
    return !!(returningMetadata?.has_activity);
  }, [returningMetadata]);

  // Refs for auto-scroll logic
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  // Derived state
  const isAwardedToday = useMemo(() => {
    if (!lastActivityDate) return false;
    const today = new Date().toISOString().split('T')[0];
    return lastActivityDate === today;
  }, [lastActivityDate]);

  // Side Effects: Initialization
  useEffect(() => {
    initGamification();
  }, [initGamification]);

  // Side Effects: Background refresh for missions
  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  // Side Effects: Restore active welcome/offer flow quick reply chips on page mount/remount
  useEffect(() => {
    if (messages.length > 0 && dashboardState !== 'CONVERSATIONAL') {
      if (dashboardState === 'AWAITING_REMEMBER') {
        setQuickReplyOptions([
          { label: t('v2DashboardChip_yes'), value: 'yes' },
          { label: t('v2DashboardChip_no'), value: 'no' }
        ]);
        setShowQuickReplies(true);
      } else if (dashboardState === 'LESSON_RESUME_OFFER') {
        setQuickReplyOptions([
          { label: t('v2DashboardChip_yes_continue'), value: 'yes' },
          { label: t('v2DashboardChip_no_simple'), value: 'no' }
        ]);
        setShowQuickReplies(true);
      } else if (dashboardState === 'EXERCISE_RESUME_OFFER' || dashboardState === 'SELF_TEST_RESUME_OFFER' || dashboardState === 'SPECIFIC_LESSON_RESUME_OFFER') {
        setQuickReplyOptions([
          { label: t('v2DashboardChip_yes_simple'), value: 'yes' },
          { label: t('v2DashboardChip_no_simple'), value: 'no' }
        ]);
        setShowQuickReplies(true);
      } else if (dashboardState === 'LESSON_COMPLETE_OFFER') {
        setQuickReplyOptions([
          { label: t('v2DashboardChip_yes_choose'), value: 'yes' },
          { label: t('v2DashboardChip_no_simple'), value: 'no' }
        ]);
        setShowQuickReplies(true);
      }
    }
  }, [t, messages.length, dashboardState, setQuickReplyOptions, setShowQuickReplies]);

  // Side Effects: Sequential Onboarding & Retrospective Flow
  useEffect(() => {
    let active = true;

    const runFlow = async () => {
      // If we already have message history, exit the welcome flow immediately to preserve conversation and visual states
      if (messages.length > 0) {
        setIsGreetingTyping(false);
        setLoadingRetroText(false);
        return;
      }

      // TODO: In the future, fetch chat history and state from the database
      // using the GET /api/agent/history endpoint to auto-populate if local cache is cleared.

      // 1. Initial State: show typing indicator for the welcome greeting for exactly 1 second
      setIsGreetingTyping(true);
      setShowGreeting(false);
      setLoadingRetroText(false);
      setShowRetroText(false);
      setShowQuickReplies(false);

      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (!active) return;

      // 2. Display the greeting text ("Hi <name>...")
      setIsGreetingTyping(false);
      setShowGreeting(true);

      // Let the first greeting bubble render completely with a smooth transition
      await new Promise((resolve) => setTimeout(resolve, 1600));
      if (!active) return;

      // 3. Show the second typing bubble for the server retrospective data
      setLoadingRetroText(true);

      // 4. Send the call to the server
      try {
        let meta = returningMetadata;
        if (!meta) {
          const userService = new UserService();
          meta = await userService.getReturningUserSessionMetadata();
          if (!active) return;
          setReturningMetadata(meta);
        }

        // Natural premium delay so the user actually sees the second typing bubble transition smoothly
        await new Promise((resolve) => setTimeout(resolve, 1000));
        if (!active) return;

        setLoadingRetroText(false);

        if (meta?.has_activity) {
          // 5. Display retrospective bubble
          setShowRetroText(true);
          setRetroStep('retrospective_flow');
          setDashboardState('AWAITING_REMEMBER');
          setQuickReplyOptions([
            { label: t('v2DashboardChip_yes'), value: 'yes' },
            { label: t('v2DashboardChip_no'), value: 'no' }
          ]);
          // 6. Only after retrospective renders, show the quick reply chips!
          setShowQuickReplies(true);
        } else {
          setRetroStep('none');
          setDashboardState('CONVERSATIONAL');
          const genderVal = user_attributes?.gender;
          const isFemale = genderVal === 2 || genderVal === '2' || genderVal === 'female';
          const isNeutral = genderVal === 3 || genderVal === '3' || genderVal === 'neutral';
          
          const welcomeKey = isFemale 
            ? 'v2DashboardGreeting_first_time_female' 
            : (isNeutral ? 'v2DashboardGreeting_first_time_neutral' : 'v2DashboardGreeting_first_time_male');
            
          addAssistantMessage(`${t(welcomeKey)} SHOW_CATALOG`);
        }
      } catch (err) {
        console.error('Failed to fetch returning session metadata:', err);
        if (!active) return;
        setLoadingRetroText(false);
        setRetroStep('none');
      }
    };

    runFlow();

    return () => {
      active = false;
    };
  }, [t]);

  // Side Effects: Auto-scroll
  useEffect(() => {
    const content = scrollContentRef.current;
    const container = document.querySelector('.v2-dashboard-chat-container');
    if (!content || !container) return;

    const observer = new ResizeObserver(() => {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const handleMissionPress = useCallback((p: MissionItem) => {
    if (p.type === 'lesson') {
      navigate(RouterConstant.ROUTERS_PATH.PLAY_LESSON, {
        state: {
          lessonId: p.lesson_id,
          topicName: p.full_topic_label,
          levelName: p.level_name,
          topicId: p.topic_id,
          levelId: p.level_id,
          subTopicId: p.sub_topic_id,
          lastInteractionId: p.last_interaction_id,
          resume: true
        }
      });
    } else {
      navigate(RouterConstant.ROUTERS_PATH.DISPLAYEXERCISE, {
        state: {
          exerciseId: p.exercise_id,
          topicName: p.full_topic_label,
          resume: true
        }
      });
    }
  }, [navigate]);

  // Handlers
  const handleSendMessage = useCallback(async (msg: string, fromChip: boolean = false) => {
    if (!msg.trim()) return;
    
    const currentState = dashboardState;
    
    if (currentState !== 'CONVERSATIONAL') {
      setShowQuickReplies(false);

      // Client-side static intercept for returning user remembering what they learned
      if (currentState === 'AWAITING_REMEMBER' && msg === 'yes') {
        setIsAgentLoadingManual(true);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsAgentLoadingManual(false);
        
        const genderVal = user_attributes?.gender;
        const isFemale = genderVal === 2 || genderVal === '2' || genderVal === 'female';
        const responseText = isFemale 
          ? t('v2DashboardGreeting_yes_response_female') 
          : t('v2DashboardGreeting_yes_response_male');

        addAssistantMessage(responseText);
        setDashboardState('ELABORATE_PROMPT');
        setRetroStep('retrospective_flow');
        return;
      }

      // Client-side intercepts for YES/NO clicks during resume/complete offers
      const isOfferState = [
        'LESSON_RESUME_OFFER',
        'EXERCISE_RESUME_OFFER',
        'SELF_TEST_RESUME_OFFER',
        'LESSON_COMPLETE_OFFER',
        'SPECIFIC_LESSON_RESUME_OFFER'
      ].includes(currentState);

      if (isOfferState && fromChip) {
        if (msg === 'yes') {
          // Navigate immediately to the last studied step or choice on the client side!
          if (currentState === 'LESSON_RESUME_OFFER' || currentState === 'SPECIFIC_LESSON_RESUME_OFFER') {
            const incompleteLesson = returningMetadata?.dashboard_activity?.incomplete_lesson;
            if (incompleteLesson) {
              navigate(RouterConstant.ROUTERS_PATH.PLAY_LESSON, {
                state: {
                  lessonId: incompleteLesson.lesson_id,
                  topicName: incompleteLesson.topic_name,
                  levelName: incompleteLesson.level_name,
                  topicId: incompleteLesson.topic_id,
                  levelId: incompleteLesson.level_id,
                  subTopicId: incompleteLesson.sub_topic_id,
                  lastInteractionId: incompleteLesson.last_interaction_id,
                  resume: true
                }
              });
              return;
            }
          } else if (currentState === 'EXERCISE_RESUME_OFFER') {
            const incompleteExercise = returningMetadata?.dashboard_activity?.incomplete_exercise;
            if (incompleteExercise) {
              navigate(RouterConstant.ROUTERS_PATH.DISPLAYEXERCISE, {
                state: {
                  exerciseId: incompleteExercise.exercise_id,
                  topicName: incompleteExercise.topic_name,
                  resume: true
                }
              });
              return;
            }
          } else if (currentState === 'SELF_TEST_RESUME_OFFER') {
            navigate(RouterConstant.ROUTERS_PATH.SELFTESTSTART);
            return;
          } else if (currentState === 'LESSON_COMPLETE_OFFER') {
            // Render the catalog immediately for choosing the next topic!
            setIsAgentLoadingManual(true);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            setIsAgentLoadingManual(false);

            addAssistantMessage("SHOW_CATALOG");
            setDashboardState('CONVERSATIONAL');
            setRetroStep('none');
            setShowQuickReplies(false);
            return;
          }
        } else if (msg === 'no') {
          // Immediately display the CatalogGrid with fallback intro on the client side!
          setIsAgentLoadingManual(true);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          setIsAgentLoadingManual(false);

          addAssistantMessage("SHOW_CATALOG");
          setDashboardState('CONVERSATIONAL');
          setRetroStep('none');
          setShowQuickReplies(false);
          return;
        }
      }

      try {
        let userMsg = msg;
        if (fromChip) {
          if (msg === 'yes') {
            if (currentState === 'AWAITING_REMEMBER') userMsg = t('v2DashboardChip_yes');
            else if (currentState === 'LESSON_RESUME_OFFER') userMsg = t('v2DashboardChip_yes_continue');
            else if (currentState === 'LESSON_COMPLETE_OFFER') userMsg = t('v2DashboardChip_yes_choose');
            else userMsg = t('v2DashboardChip_yes_simple');
          } else if (msg === 'no') {
            if (currentState === 'AWAITING_REMEMBER') userMsg = t('v2DashboardChip_no');
            else userMsg = t('v2DashboardChip_no_simple');
          }
        }

        if (!fromChip) {
          addUserMessage(userMsg);
        } else {
          // Natural premium typing delay only for chip actions
          setIsAgentLoadingManual(true);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          setIsAgentLoadingManual(false);
        }

        const response = await talk({
          message: userMsg,
          skipAddUserMessage: true,
          context: {
            current_page: 'dashboard',
            metadata: {
              current_streak: currentStreak,
              total_points: totalPoints,
              gender: (user_attributes?.gender === 2 || user_attributes?.gender === '2' || user_attributes?.gender === 'female') ? 'female' : ((user_attributes?.gender === 3 || user_attributes?.gender === '3' || user_attributes?.gender === 'neutral') ? 'neutral' : 'male'),
              is_user_return: true,
              dashboard_state: currentState,
              last_bot_message: [...messages].reverse().find(m => m.isBot)?.text || ''
            }
          }
        });

        if (response && response.data && response.data.next_state) {
          const nextState = response.data.next_state;
          setDashboardState(nextState);

          if (nextState === 'CONVERSATIONAL') {
            setRetroStep('none');
            setShowQuickReplies(false);
          } else {
            setRetroStep('retrospective_flow');
            
            // Set dynamic chip labels based on the next state
            if (nextState === 'LESSON_RESUME_OFFER') {
              setQuickReplyOptions([
                { label: t('v2DashboardChip_yes_continue'), value: 'yes' },
                { label: t('v2DashboardChip_no_simple'), value: 'no' }
              ]);
              setShowQuickReplies(true);
            } else if (nextState === 'EXERCISE_RESUME_OFFER' || nextState === 'SPECIFIC_LESSON_RESUME_OFFER') {
              setQuickReplyOptions([
                { label: t('v2DashboardChip_yes_simple'), value: 'yes' },
                { label: t('v2DashboardChip_no_simple'), value: 'no' }
              ]);
              setShowQuickReplies(true);
            } else if (nextState === 'SELF_TEST_RESUME_OFFER') {
              setQuickReplyOptions([
                { label: t('v2DashboardChip_yes_simple'), value: 'yes' },
                { label: t('v2DashboardChip_no_simple'), value: 'no' }
              ]);
              setShowQuickReplies(true);
            } else if (nextState === 'LESSON_COMPLETE_OFFER') {
              setQuickReplyOptions([
                { label: t('v2DashboardChip_yes_choose'), value: 'yes' },
                { label: t('v2DashboardChip_no_simple'), value: 'no' }
              ]);
              setShowQuickReplies(true);
            } else {
              setShowQuickReplies(false);
            }
          }
        } else {
          setDashboardState('CONVERSATIONAL');
          setRetroStep('none');
          setShowQuickReplies(false);
        }
      } catch (err) {
        console.error('Dashboard chat agent communication failed:', err);
        setDashboardState('CONVERSATIONAL');
        setRetroStep('none');
        setShowQuickReplies(false);
      }
    } else {
      // Normal flow - talk() will automatically handle adding the user bubble exactly once
      try {
        const response = await talk({
          message: msg,
          context: {
            current_page: 'dashboard',
            metadata: {
              current_streak: currentStreak,
              total_points: totalPoints,
              gender: (user_attributes?.gender === 2 || user_attributes?.gender === '2' || user_attributes?.gender === 'female') ? 'female' : ((user_attributes?.gender === 3 || user_attributes?.gender === '3' || user_attributes?.gender === 'neutral') ? 'neutral' : 'male'),
              dashboard_state: 'CONVERSATIONAL',
              last_bot_message: [...messages].reverse().find(m => m.isBot)?.text || ''
            }
          }
        });

        // If the server intercepts the conversational state to propose a resume offer, update client state & chips
        if (response && response.data && response.data.next_state) {
          const nextState = response.data.next_state;
          if (nextState !== 'CONVERSATIONAL') {
            setDashboardState(nextState);
            setRetroStep('retrospective_flow');

            if (nextState === 'LESSON_RESUME_OFFER') {
              setQuickReplyOptions([
                { label: t('v2DashboardChip_yes_continue'), value: 'yes' },
                { label: t('v2DashboardChip_no_simple'), value: 'no' }
              ]);
              setShowQuickReplies(true);
            } else if (nextState === 'EXERCISE_RESUME_OFFER' || nextState === 'SPECIFIC_LESSON_RESUME_OFFER') {
              setQuickReplyOptions([
                { label: t('v2DashboardChip_yes_simple'), value: 'yes' },
                { label: t('v2DashboardChip_no_simple'), value: 'no' }
              ]);
              setShowQuickReplies(true);
            } else if (nextState === 'SELF_TEST_RESUME_OFFER') {
              setQuickReplyOptions([
                { label: t('v2DashboardChip_yes_simple'), value: 'yes' },
                { label: t('v2DashboardChip_no_simple'), value: 'no' }
              ]);
              setShowQuickReplies(true);
            } else if (nextState === 'LESSON_COMPLETE_OFFER') {
              setQuickReplyOptions([
                { label: t('v2DashboardChip_yes_choose'), value: 'yes' },
                { label: t('v2DashboardChip_no_simple'), value: 'no' }
              ]);
              setShowQuickReplies(true);
            } else {
              setShowQuickReplies(false);
            }
          }
        }
      } catch (err) {
        console.error('Dashboard chat agent communication failed:', err);
      }
    }
  }, [talk, currentStreak, totalPoints, dashboardState, userInfo, t, setIsAgentLoadingManual]);

  return {
    t,
    navigate,
    userInfo,
    currentStreak,
    totalPoints,
    isAwardedToday,
    loadingGamification,
    progressMissions,
    loadingMissions,
    fetchMissions,
    scrollContentRef,
    chatBottomRef,
    messages,
    isAgentLoading,
    handleSendMessage,
    handleMissionPress,
    openModesModal,
    
    // New returning user states
    returningMetadata,
    loadingMetadata,
    isGreetingTyping,
    showGreeting,
    loadingRetroText,
    showRetroText,
    isAgentLoadingManual,
    retroStep,
    isReturningUser,
    showQuickReplies,
    quickReplyOptions,
    setShowQuickReplies,
    user_attributes,
  };
};
